import { memo, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { config } from "../../../config/config";
import {
  DETAIL_TAB2POINT_CLOUD_TYPE,
  EDisplayType,
  EUpdateStatus,
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { ITextScale } from "../../../viewer/config/ViewerConfig";
import {
  IMeasurement,
  IMeasurementHandler,
  MeasurementType,
} from "../../../viewer/measurement/types";
import CalcResultDetail from "../Clculation/CalcResultDetail";
import HeatMapDialog from "../Clculation/HeatMapDialog";
import { ProjectVolumeList } from "../Clculation/ProjectVolumeList";
import CalcContext from "../Contexts/CalcContext";
import { UpdateInfoContext } from "../Contexts/UpdateInfoContext";
import { debounce } from "lodash";
import { Box } from "@mui/material";
import axios from "axios";

type Props = {};

export type VolumeCalcColumn = {
  id: string;
  pointName: string;
  x: number;
  y: number;
  z: number;
};

export const CalcDrawerContent = memo((props: Props) => {
  //language
  const { t } = useTranslation();
  const {
    viewer,
    currentViewerMode,
    currentPointCloud,
    projectDetail,
    detailTab,
  } = useContext(ProjectDetailContext);
  const {
    measurementType,
    setMeasurementType,
    setOnEditingMode,
    onEditingMode,
  } = useContext(CalcContext);
  const { displayType } = useContext(ProjectDetailContext);
  const { setUpdateAt, updateAt } = useContext(UpdateInfoContext);

  /*****************************************
      state
    ******************************************/
  const [openCalcResultDetailDialog, setOpenCalcResultDetailDialog] =
    useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IMeasurementHandler>();
  const [editedItem, setEditedItem] = useState<IMeasurementHandler>();
  const [selectedCalcResult, setSelectedCalcResult] = useState<
    IMeasurementHandler[]
  >([]);
  const [rowsVolumeCalc, setRowsVolumeCalc] = useState<VolumeCalcColumn[]>([]);
  const [showHeatMapBtn, setShowHeatMapBtn] = useState<boolean>(false);
  const [editType, setEditType] = useState<string>("PICK_POINT");
  const [showHeatMap, setShowHeatMap] = useState<boolean>(false);
  const [beforeUpdateAt, setBeforeUpdateAt] = useState<string>(updateAt);

  const editTypeItems = [
    {
      label: t("PICK_POINT"),
      value: "PICK_POINT",
    },
    {
      label: t("MOVE_XY"),
      value: "MOVE_XY",
    },
    {
      label: t("MOVE_Z"),
      value: "MOVE_Z",
    },
  ];

  /*****************************************
      Effect
    ******************************************/
  useEffect(() => {
    const abortController = new AbortController();
    const abortControllerSignal = abortController.signal;

    if (currentViewerMode === EViewerMode.CALCULATION) {
      setCalcResultList(abortControllerSignal);
    }

    return () => {
      abortController.abort();
      selectedItem?.id && viewer?.setHeatMapVisibility(selectedItem?.id, false);
      viewer?.deleteMeasurement();
      finishMeasurement();
      setOpenCalcResultDetailDialog(false);
      setSelectedItem(null);
      setSelectedCalcResult([]);
      setMeasurementType(null);
    };
  }, [currentViewerMode, currentPointCloud]);

  useEffect(() => {
    if (currentViewerMode === EViewerMode.CALCULATION) {
      measurementType ? startMeasurement() : finishMeasurement();
    } else {
      selectedItem?.id && viewer?.setHeatMapVisibility(selectedItem?.id, false);
      viewer?.deleteMeasurement();
      finishMeasurement();
      setOpenCalcResultDetailDialog(false);
      setSelectedItem(null);
      setSelectedCalcResult([]);
      setMeasurementType(null);
    }
    return () => {};
  }, [measurementType]);

  // useEffect(() => {
  //   return () => {
  //     uploadCalcResult(selectedCalcResult);
  //   };
  // }, [currentCalculationMode]);

  useEffect(() => {
    if (currentViewerMode === EViewerMode.CALCULATION && !onEditingMode) {
      startMeasurement();
    }
  }, [selectedCalcResult.length, onEditingMode]);

  useEffect(() => {
    const shouldStart = !onEditingMode;

    if (shouldStart) startMeasurement();

    return () => {
      if (shouldStart) finishMeasurement();
    };
  }, [onEditingMode]);

  useEffect(() => {
    selectedCalcResult.map((result) => {
      if (result?.type === MeasurementType.VOLUME_CALCULATION) {
        if (result.id !== selectedItem?.id) {
          viewer?.setHeatMapVisibility(result?.id, false);
        }
      }
    });

    if (showHeatMap) {
      viewer?.setHeatMapVisibility(selectedItem?.id, true);
    }

    setShowHeatMapBtn(
      selectedItem?.type === MeasurementType.VOLUME_CALCULATION
    );

    return () => {
      setShowHeatMapBtn(false);
      // console.log(selectedItem?.id);
      viewer?.setHeatMapVisibility(selectedItem?.id, false);
    };
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItem?.getVisibility()) {
      viewer?.setHeatMapVisibility(selectedItem?.id, showHeatMap);
    }
  }, [showHeatMap]);

  /*****************************************
      function
    ******************************************/
  // heatMap表示ボタン
  const toggleSetShowHeatMap = (show?: boolean) => {
    setShowHeatMap(show === undefined ? !showHeatMap : show);
  };

  // 体積計算のピック方法切替
  const handlePickType = (v: string, selected?: IMeasurementHandler) => {
    setEditType(v);
    if (onEditingMode) {
      finishEdit();
      startEdit(v, selected);
    }
  };

  // 編集ボタン
  // const toggleEditBtn = () => {
  //   if (currentCalculationMode === ECalculationMode.EDITING) {
  //     setCurrentCalculationMode(ECalculationMode.CREATING);
  //   } else {
  //     setCurrentCalculationMode(ECalculationMode.EDITING);
  //   }
  // };

  // 編集開始
  const startEdit = (type?: string, selected?: IMeasurementHandler) => {
    setOnEditingMode(true);
    const editId = selected ? selected.id : selectedItem?.id;

    viewer.setMeasurementVisibility(editId, {
      text: true,
      measurement: true,
    });
    if (!type) type = editType;
    switch (type) {
      case editTypeItems[0].value:
        viewer.editMeasurement(
          editId,
          { direction: "default", dataflow: "latest_val_on_release" },
          [{ onUpdateComplete: onPositionUpdate }]
        );
        break;
      case editTypeItems[1].value:
        viewer.editMeasurement(
          editId,
          { direction: "xy", dataflow: "latest_val_on_release" },
          [{ onUpdateComplete: onPositionUpdate }]
        );
        break;
      case editTypeItems[2].value:
        viewer.editMeasurement(
          editId,
          { direction: "z", dataflow: "latest_val_on_release" },
          [{ onUpdateComplete: onPositionUpdate }]
        );
        break;
      default:
        viewer.editMeasurement(
          editId,
          { direction: "default", dataflow: "latest_val_on_release" },
          [{ onUpdateComplete: onPositionUpdate }]
        );
        break;
    }

    viewer.setHeatMapVisibility(selectedItem?.id, showHeatMap);
    viewer.updateRenderer();
  };

  // 編集終了
  const finishEdit = () => {
    if (!editedItem) return;

    setOnEditingMode(false);
    viewer.updateEditMeasurement(projectDetail.id);
    viewer.finishMeasurement();
  };

  const onPositionUpdate = () => {
    const offset = viewer.getOffset();
    const pointsList = selectedItem?._points.map((data: any, index: any) => {
      return {
        id: index,
        pointName: index + 1,
        x: Number((data.x - offset.x).toFixed(3)),
        y: Number((data.y - offset.y).toFixed(3)),
        z: Number((data.z - offset.z).toFixed(3)),
      };
    }) as VolumeCalcColumn[];
    setRowsVolumeCalc(pointsList);
  };

  // 計測開始
  const startMeasurement = async () => {
    const textScale: ITextScale =
      displayType === EDisplayType.iOS
        ? { orthographic: 0.04, perspective: 0.12 }
        : { orthographic: 0.09, perspective: 0.18 };

    if (measurementType === MeasurementType.SURFACE) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.SURFACE },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.FLAT_AREA_CALCULATION) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.FLAT_AREA_CALCULATION },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.HORIZONTAL) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.HORIZONTAL },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.VERTICAL) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.VERTICAL },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.TWO_POINT) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.TWO_POINT },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.MULTIPLE_POINT) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.MULTIPLE_POINT },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.VOLUME_CALCULATION) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          {
            type: MeasurementType.VOLUME_CALCULATION,
            lowerBound: {
              minColor: 0xa5e5ff,
              maxColor: 0x0000ff,
            },
            upperBound: {
              minColor: 0xffff00,
              maxColor: 0xff0000,
            },
          },
          textScale
        )
      );
    }
  };

  // 計測終了
  const finishMeasurement = async () => {
    viewer.cancelMeasurement();
    viewer.finishMeasurement();
  };

  const addSelectedCalcResult = (data: IMeasurementHandler) => {
    if (data?.id === 1) {
      uploadCalcResult([data]);
      setSelectedCalcResult([data]);
    } else {
      uploadCalcResult([...selectedCalcResult, data]);
      setSelectedCalcResult([...selectedCalcResult, data]);
    }

    setSelectedItem(data);
    setOpenCalcResultDetailDialog(true);
  };

  const initMeasurement = async (abortControllerSignal: AbortSignal) => {
    if (selectedCalcResult.length <= 0) {
      const measurements = await setCalcResultList(abortControllerSignal);
    }
  };

  const setCalcResultList = async (abortControllerSignal: AbortSignal) => {
    try {
      viewer.setMeasurementId(0);
      viewer.initMeasurementMap();

      const apiUrl = `${config.serverHost}/api/v1/project/${
        projectDetail.id
      }/point-cloud/${DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)}/measurement`;

      const response = await axios.get(apiUrl, {
        withCredentials: true,
        signal: abortControllerSignal,
      });

      const measurements: IMeasurementHandler[] = response.data;
      const measurementsTmp: IMeasurementHandler[] = [];

      const textScale: ITextScale =
        displayType === EDisplayType.iOS
          ? { orthographic: 0.04, perspective: 0.12 }
          : { orthographic: 0.09, perspective: 0.18 };

      for (const val of measurements) {
        viewer.poligonIndicesCount = 0;
        if (val._points.length > 0) {
          const initialMeasurement = await viewer.createInitialMeasurement(
            { type: val.type },
            val,
            textScale
          );
          initMeasurement && measurementsTmp.push(initialMeasurement);
        }
      }

      setSelectedCalcResult(measurementsTmp);
      return measurements;
    } catch (e) {
      // console.error(e);
      return [];
    }
  };

  const uploadCalcResult = async (calcArray: IMeasurementHandler[]) => {
    const calcNewArray: IMeasurement[] = [];

    const afterUpdateAt = new Date().toISOString();

    for (const val of calcArray) {
      calcNewArray.push({
        id: val.id,
        type: val.type,
        _points: val._points,
        value: val.value,
        visible: val.getVisibility() ?? false,
        updateAt: afterUpdateAt,
      });
    }
    setUpdateAt(afterUpdateAt);

    await axios
      .put(
        `${config.serverHost}/api/v1/project/${
          projectDetail.id
        }/point-cloud/${DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)}/measurement`,
        { measurements: calcNewArray },
        { withCredentials: true }
      )
      .then(() => {
        setUpdateAt("saved");

        setTimeout(() => {
          setUpdateAt(afterUpdateAt);
        }, 3000);
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 403) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_FORBIDDEN);
          } else if (err.response.status === 500) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_ERROR);
          } else {
            setUpdateAt(EUpdateStatus.SAVE_FAILED);
          }
        }

        setTimeout(() => {
          setUpdateAt(beforeUpdateAt);
        }, 3000);
      });
  };

  const deleteCalcResult = async (deleteId: number) => {
    const calcNewArray: IMeasurement[] = [];
    const calcHandlerNewArray: IMeasurementHandler[] = [];

    viewer?.setHeatMapVisibility(deleteId, false);
    viewer?.deleteMeasurement(deleteId);

    // clear selected item and set mode to creating if the item is being edited
    if (
      (selectedItem?.id === deleteId &&
        onEditingMode &&
        editedItem?.id === selectedItem?.id) ||
      editedItem?.id === deleteId
    ) {
      setOnEditingMode(false);
      setSelectedItem(null);

      setEditedItem(null);
    }

    const afterUpdateAt = new Date().toISOString();

    for (const val of selectedCalcResult) {
      if (val.id !== deleteId) {
        calcNewArray.push({
          id: val.id,
          type: val.type,
          _points: val._points,
          value: val.value,
          visible: val.getVisibility() ?? false,
          updateAt: afterUpdateAt,
        });
        calcHandlerNewArray.push(val);
      }
    }

    setUpdateAt(afterUpdateAt);

    // changePickedMeasurementTexture()

    setSelectedCalcResult(calcHandlerNewArray);

    await axios
      .put(
        `${config.serverHost}/api/v1/project/${
          projectDetail.id
        }/point-cloud/${DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)}/measurement`,
        { measurements: calcNewArray },
        { withCredentials: true }
      )
      .then(() => {
        setUpdateAt("saved");

        setTimeout(() => {
          setUpdateAt(afterUpdateAt);
        }, 3000);
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 403) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_FORBIDDEN);
          } else if (err.response.status === 500) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_ERROR);
          } else {
            setUpdateAt(EUpdateStatus.SAVE_FAILED);
          }
        }

        setTimeout(() => {
          setUpdateAt(beforeUpdateAt);
        }, 3000);
      });
  };

  const uploadCalcResultDebounce = useCallback(
    debounce((calcArray: IMeasurementHandler[]) => {
      uploadCalcResult(calcArray);
    }, 1000),
    [detailTab]
  );

  const handleSetSelectedCalcResult = (calcArray: IMeasurementHandler[]) => {
    setSelectedCalcResult(calcArray);
    uploadCalcResultDebounce(calcArray);
  };

  // UI
  // 計測詳細閉じるボタン
  const handleClickCancel = () => {
    setOpenCalcResultDetailDialog(false);
    setSelectedItem(null);
    viewer.changeAppearanceBySelected(null);
  };

  const resCalcType = (data: any) => {
    if (!data) return;
    const type: string = data.type;
    if (type === MeasurementType.VOLUME_CALCULATION) {
      return t("VOLUME");
    } else if (type === MeasurementType.TWO_POINT) {
      return t("DISTANCE_TWO_POINTS");
    } else if (type === MeasurementType.MULTIPLE_POINT) {
      return t("DISTANCE_MULTIPLE_POINTS");
    } else if (type === MeasurementType.VERTICAL) {
      return t("VERTICAL_DISTANCE");
    } else if (type === MeasurementType.HORIZONTAL) {
      return t("HORIZONTAL_DISTANCE");
    } else if (type === MeasurementType.FLAT_AREA_CALCULATION) {
      return t("FLAT_AREA");
    } else if (type === MeasurementType.SURFACE) {
      return t("SURFACE_DISTANCE");
    }
  };

  const setSelectedItemFromList = (any: any) => {
    console.log("setSelectedItemFromList", any);

    setSelectedItem(any);
  };

  return (
    <Box
      style={{
        display: "flex",
        padding: "4px 16px",
        height: `calc(100% - 8px)`,
        gap: "var(--space-space--4, 4px)",
      }}
    >
      <ProjectVolumeList
        setOpenCalcResultDetailDialog={setOpenCalcResultDetailDialog}
        setSelectedItem={setSelectedItem}
        setEditedItem={setEditedItem}
        openCalcResultDetailDialog={openCalcResultDetailDialog}
        resCalcType={resCalcType}
        selectedCalcResult={selectedCalcResult}
        setSelectedCalcResult={setSelectedCalcResult}
        selectedItem={selectedItem}
        handleClickCancel={handleClickCancel}
        handleSetSelectedCalcResult={handleSetSelectedCalcResult}
        deleteCalcResult={deleteCalcResult}
        showHeatMap={showHeatMap}
        handlePickType={handlePickType}
        toggleSetShowHeatMap={toggleSetShowHeatMap}
      />
      <CalcResultDetail
        selectedItem={selectedItem}
        handleClickCancel={handleClickCancel}
        showHeatMap={showHeatMap}
        rowsVolumeCalc={rowsVolumeCalc}
        setRowsVolumeCalc={setRowsVolumeCalc}
        startEdit={startEdit}
        finishEdit={finishEdit}
      />
      {showHeatMap &&
        selectedItem?.type === MeasurementType.VOLUME_CALCULATION && (
          <HeatMapDialog item={selectedItem} />
        )}
    </Box>
  );
});
